import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import containerTemplate from './association-section-for-iff.pug';
import layoutTemplate from './double-list-box-layout.pug';

const trim = Function.prototype.call.bind(String.prototype.trim);

/**
 * Normalizes an audience coming form IFF analyzer. It removes whitespaces
 * around the name as well as around the separators /.
 * For example, a raw name `"  Lipstick  /  Lip Liner and Eyeliner  /  Eye Shadow  "`
 * will be turned into `"Lipstick/Lip Liner and Eyeliner/Eye Shadow"`.
 * Another example: `"  /  Real Estate / Residential Properties  "` will be turned into
 * `"/Real Estate/Residential Properties"`.
 * @param {String} audienceName the raw audience name, parsed by IFF analyzer.
 * It can contain extra whitespaces around the text, as well as around the separators /
 * @returns {String} the normalized audience name
 */
const normalizeAudienceName = (audienceName = '') => {
  const audienceNamePath = audienceName.split('/');
  const addLeadingSlash = !((_.first(audienceNamePath) || '').trim());
  const trimmedParts = _.chain(audienceNamePath)
    .filter(Boolean)
    .map(trim)
    .join('/')
    .value();
  return `${addLeadingSlash ? '/' : ''}${trimmedParts}`;
};

/**
 * Contructs and hold a reference to AudienceTargetInfo.
 * AudienceTargetInfo is expensive to construct as it needs
 * to request the data from the server. This class is designed to
 * be used by code that establishes a session during which
 * AudienceTargetInfo (audience selection control) is needed.
 */
export class AudienceSelectorLegacyViewManager {
  constructor({ perfMarker }) {
    const AudienceAssociationView
      = DependencyResolver.get('campaignui/feature/audience/association/view/base-association-view');
    this.audienceTargetsView = new AudienceAssociationView({
      scope: {}, // this is needed to request audiences for all delivery channels
      campaignType: null, // this is needed to request audiences for all delivery channels
      associationType: null, // this is needed to remove all "create audience" links
      refreshOnInitialize: true,
      shouldShowUiBlockOnRender: false,
      customizedTemplates: {
        dlbContainerTemplate: containerTemplate,
        dlbLayoutTemplate: layoutTemplate,
      },
      isFromIFFunction: true,
      perfMarker,
    });
    this.audienceTargetsView.canExpandAssociation = true;
    this.audienceTargetsView.render();
    this.audienceTargetsView.expandAssociation();
  }
}

export class AudienceSelector extends React.PureComponent {
  constructor(props) {
    super(props);

    this.audienceTargetsView = props.audienceTargetsView;

    this.state = {
      loaded: false,
    };

    this.audienceTargetsView.ensureLoadedAsync().then(() => {
      this.mapTargetingIdsToCriterions();
      this.setState({ loaded: true });
      this.subscribeToSelectionChanges();
      this.onAudienceListChanged(true);
    });

    this.onAudienceListChanged = this.onAudienceListChanged.bind(this);
  }

  componentWillUnmount() {
    this.unsubscribeFromSelectionChanges();
  }

  onAudienceListChanged(isInitialLoad = false) {
    this.props.onAudienceListChanged(this.getAudienceList(), isInitialLoad);
  }

  getAudienceList() {
    const { associationList } = this.audienceTargetsView.getLists();
    return _.map(associationList, _.property('Name'));
  }

  mapAudienceNamesToCriterion = audiencesList =>
    _.chain(audiencesList)
      .map(this.mapAudienceNameToCriterion)
      .compact()
      .value();

  mapAudienceNameToCriterion = (audienceName) => {
    const normalizedAudienceName = normalizeAudienceName(audienceName);
    return _.find(this.audienceTargetsView.doubleListBoxViewModel.allAudiences, audience =>
      audience.Name === normalizedAudienceName);
  };

  mapTargetingIdsToCriterions = () => {
    this.criterions = {
      associationList: this.mapAudienceNamesToCriterion(this.props.audienceList),
      exclusionList: [],
    };
    this.audienceTargetsView.setLists(this.criterions);
  };

  subscribeToSelectionChanges() {
    if (!this.isSubscribedToSelectionChanges) {
      this.audienceTargetsView.on('selectionChanged', () => this.onAudienceListChanged());
      this.isSubscribedToSelectionChanges = true;
    }
  }

  unsubscribeFromSelectionChanges() {
    if (this.isSubscribedToSelectionChanges) {
      this.audienceTargetsView.off('selectionChanged', () => this.onAudienceListChanged());
      this.isSubscribedToSelectionChanges = false;
    }
  }

  render() {
    return this.state.loaded ? (
      <div {...(this.props.className ? { className: this.props.className } : {})}>
        <BackboneInstanceWrapper
          instance={this.audienceTargetsView}
          managed
        />
      </div>
    ) : null;
  }
}

AudienceSelector.propTypes = {
  audienceTargetsView: PropTypes.shape({
    render: PropTypes.func.isRequired,
  }).isRequired,
  audienceList: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  data: PropTypes.shape({
    Includes: PropTypes.array,
    Excludes: PropTypes.array,
  }),
  onAudienceListChanged: PropTypes.func.isRequired,
};

AudienceSelector.defaultProps = {
  audienceList: [],
  className: undefined,
  data: {},
};
